import type {
  ProductAccessoryProduct,
  ProductDetailData,
} from '@eliadmin/core/src/api/products/types/product-types';

export const RS_MOTO_ACCESSORY_TYPES = {
  optional: 'accessory',
  required: 'required_accessory',
  productVariant: 'product_variant',
} as const;

export type RsMotoAccessoryType =
  (typeof RS_MOTO_ACCESSORY_TYPES)[keyof typeof RS_MOTO_ACCESSORY_TYPES];

export type RsMotoAccessoryProduct = ProductAccessoryProduct & {
  accessory_product?: {
    id: number;
    type?: RsMotoAccessoryType | string | null;
  };
};

export type RsMotoProductWithAccessories = Omit<
  ProductDetailData,
  'accessories' | 'accessory_product_variants'
> & {
  accessories?: RsMotoAccessoryProduct[] | null;
  required_accessories?: RsMotoAccessoryProduct[] | null;
  accessory_product_variants?: RsMotoAccessoryProduct[] | null;
};

export type RsMotoAccessoryGroups = {
  optional: RsMotoAccessoryProduct[];
  required: RsMotoAccessoryProduct[];
  productVariants: RsMotoAccessoryProduct[];
};

function isAccessoryProduct(value: unknown): value is RsMotoAccessoryProduct {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'number'
  );
}

function toAccessoryProducts(value: unknown): RsMotoAccessoryProduct[] {
  return Array.isArray(value) ? value.filter(isAccessoryProduct) : [];
}

function relationType(accessory: RsMotoAccessoryProduct): string | null {
  return accessory.accessory_product?.type ?? null;
}

/**
 * Normalizes both the new split response and the former mixed `accessories`
 * response. Relation metadata wins over the array in which an item arrived.
 */
export function normalizeRsMotoAccessoryGroups(product: unknown): RsMotoAccessoryGroups {
  const source =
    typeof product === 'object' && product !== null
      ? (product as Record<string, unknown>)
      : ({} as Record<string, unknown>);

  const optional = new Map<number, RsMotoAccessoryProduct>();
  const required = new Map<number, RsMotoAccessoryProduct>();
  const productVariants = new Map<number, RsMotoAccessoryProduct>();

  const addByType = (accessory: RsMotoAccessoryProduct, fallback: RsMotoAccessoryType): void => {
    const type = relationType(accessory) ?? fallback;

    if (type === RS_MOTO_ACCESSORY_TYPES.required) {
      required.set(accessory.id, accessory);
      return;
    }

    if (type === RS_MOTO_ACCESSORY_TYPES.productVariant) {
      productVariants.set(accessory.id, accessory);
      return;
    }

    optional.set(accessory.id, accessory);
  };

  toAccessoryProducts(source.accessories).forEach(accessory =>
    addByType(accessory, RS_MOTO_ACCESSORY_TYPES.optional),
  );
  toAccessoryProducts(source.required_accessories).forEach(accessory =>
    addByType(accessory, RS_MOTO_ACCESSORY_TYPES.required),
  );
  toAccessoryProducts(source.accessory_product_variants).forEach(accessory =>
    addByType(accessory, RS_MOTO_ACCESSORY_TYPES.productVariant),
  );

  // A required relationship must not also appear as an optional recommendation.
  required.forEach((_, id) => optional.delete(id));
  // Product alternatives are navigation choices, not products to add alongside it.
  productVariants.forEach((_, id) => {
    optional.delete(id);
    required.delete(id);
  });

  return {
    optional: Array.from(optional.values()),
    required: Array.from(required.values()),
    productVariants: Array.from(productVariants.values()),
  };
}
